/**
 * Print template positioning — shared model.
 *
 * A template is scoped to one photo aspect ratio, the same way reels and overlays are: a campaign
 * shooting 4x3 needs its own design, not just its own coordinates. So the ratio lives on the
 * template, positions are kept per guest photo orientation, and the default template is picked per
 * campaign, per type, per aspect ratio.
 *
 * Slot coordinates are normalised (0..1) against the template background image, so they survive a
 * graphic swap, a DPI change and a portrait/landscape template without migration.
 *
 * Default positions are fabricated by the API: a template that has never been configured comes
 * back with the geometry the renderer would fall back to, so the editor never invents its own.
 */

export type PrintTemplateType = 'keyring' | 'snowglobe';

/** Photo aspect ratios a campaign can shoot in. */
export type PositionAspectRatio = '1x1' | '4x3' | '3x2';

/**
 * What a template is designed for. An empty value is the legacy shape every existing keyring has:
 * one template used for every photo ratio, with the photo cover-cropped into the slot. Templates
 * created from now on name the ratio they were designed for.
 */
export type TemplateAspectRatio = PositionAspectRatio | '';

export const LEGACY_ASPECT_RATIO: TemplateAspectRatio = '';

/** Which way round the guest photo was taken. Each one gets its own set of slots. */
export type PhotoOrientation = 'horizontal' | 'vertical';

export const PHOTO_ORIENTATIONS: { value: PhotoOrientation; label: string }[] = [
  { value: 'horizontal', label: 'Horizontal photos' },
  { value: 'vertical', label: 'Vertical photos' },
];

export const DEFAULT_PHOTO_ORIENTATION: PhotoOrientation = 'horizontal';

export interface IPrintTemplatePosition {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Degrees anticlockwise, applied to the photo inside the slot. */
  rotation: number;
}

/**
 * One layout per guest photo orientation, array order being slot order — the slots carry no id
 * because every one of them receives the same photo.
 *
 * `null` means "print this one through `3x2.horizontal`", which is what most combinations do. It
 * says so in the data rather than leaving the renderer to notice that two sets happen to match.
 * `3x2.horizontal` itself is never null.
 */
export interface IPrintTemplatePositions {
  horizontal: IPrintTemplatePosition[];
  vertical: IPrintTemplatePosition[];
}

/**
 * A template is one printed design, and one design serves every photo ratio the campaign might
 * shoot — the die-cut windows do not move, only how much of the photo has to go. So the layouts are
 * keyed by ratio inside the template rather than the template being tied to one.
 *
 * `null` is a template with no layout at all, which is what legacy keyrings are: they print through
 * the old renderer.
 */
export interface IPrintTemplateLayouts {
  [ratio: string]: IPrintTemplatePositions;
}

/**
 * The combination staff lays out first and the others start from — a horizontal photo in the ratio
 * nearly every campaign shoots.
 */
export const PRIMARY_LAYOUT_RATIO: PositionAspectRatio = '3x2';
export const PRIMARY_LAYOUT_ORIENTATION: PhotoOrientation = 'horizontal';

/** Every ratio and orientation a template holds a layout for, in the order the editor walks them. */
export function layoutCombinations(): { ratio: PositionAspectRatio; orientation: PhotoOrientation }[] {
  const ratios: PositionAspectRatio[] = [ PRIMARY_LAYOUT_RATIO, '4x3', '1x1' ];
  const combinations: { ratio: PositionAspectRatio; orientation: PhotoOrientation }[] = [];

  ratios.forEach((ratio: PositionAspectRatio) => {
    PHOTO_ORIENTATIONS.forEach((orientation: { value: PhotoOrientation }) => {
      combinations.push({ ratio: ratio, orientation: orientation.value });
    });
  });

  return combinations;
}

export interface IPrintTemplate {
  id: number;
  name: string;
  type: PrintTemplateType;
  isDefault: boolean;
  isActive: boolean;
  /**
   * The template prints on the artwork GoPhoto ships with the renderer, picked by `type`. A template
   * flagged this way can come back with `file: null`, so anything reading `file.url` or `file.s3`
   * has to cope with nothing being there.
   */
  defaultTemplate?: boolean;
  file?: any;
  campaign?: { id: number; name: string; uuid?: string };
  positions?: IPrintTemplateLayouts;
  /** Guest photos the positioning editor draws into the slots, one per orientation. */
  testPhotoHorizontal?: any;
  testPhotoVertical?: any;
}

export const PRINT_TEMPLATE_TYPES: { value: PrintTemplateType; label: string }[] = [
  { value: 'keyring', label: 'Keyring' },
  { value: 'snowglobe', label: 'Snow globe' },
];

/**
 * Photo ratios in the order the rest of the CMS shows them. `label` matches the wire value used
 * across the CMS, `photoLabel` is the human form for places that talk about the photo itself.
 */
export const POSITION_ASPECT_RATIOS:
  { value: PositionAspectRatio; label: string; photoLabel: string; icon: string }[] = [
  { value: '1x1', label: '1x1', photoLabel: '1:1', icon: 'third' },
  { value: '4x3', label: '4x3', photoLabel: '4:3', icon: 'second' },
  { value: '3x2', label: '3x2', photoLabel: '3:2', icon: 'first' },
];

/** Options for picking what a template is for, legacy included. */
export const TEMPLATE_ASPECT_RATIOS:
  { value: TemplateAspectRatio; label: string; photoLabel: string; icon: string }[] = [
  { value: '', label: 'Not set', photoLabel: 'Not set (any ratio)', icon: 'first' },
  ...POSITION_ASPECT_RATIOS,
];

/** Every existing template is legacy, so that is where the filters start. */
export const DEFAULT_ASPECT_RATIO: TemplateAspectRatio = LEGACY_ASPECT_RATIO;

/**
 * Only keyring has legacy templates — snowglobe is new, so every one of them names the photo ratio
 * it was designed for and "Not set" is never an option there.
 */
export function aspectRatiosFor(type: PrintTemplateType):
  { value: TemplateAspectRatio; label: string; photoLabel: string; icon: string }[] {
  return type === 'snowglobe' ? POSITION_ASPECT_RATIOS : TEMPLATE_ASPECT_RATIOS;
}

export function supportsLegacy(type: PrintTemplateType): boolean {
  return type !== 'snowglobe';
}

/**
 * How far the photo is turned inside its slot, in degrees **anticlockwise** — 90 stands it up with
 * its top edge to the left. Horizontal and vertical photos have their own slots, so nothing has to
 * be guessed at render time.
 */
export const ROTATION_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: '0°' },
  { value: 90, label: '90°' },
  { value: 180, label: '180°' },
  { value: 270, label: '270°' },
];

/** Every print template is a 6x4 inch sheet. */
export const SHEET_LONG_SIDE_MM = 152.4;
export const SHEET_SHORT_SIDE_MM = 101.6;

/** Physical size of the sheet in mm, derived from the background image orientation. */
export function sheetSizeMm(backgroundWidth: number, backgroundHeight: number): { width: number; height: number } {
  const portrait = backgroundHeight > backgroundWidth;
  return {
    width: portrait ? SHEET_SHORT_SIDE_MM : SHEET_LONG_SIDE_MM,
    height: portrait ? SHEET_LONG_SIDE_MM : SHEET_SHORT_SIDE_MM,
  };
}

/** Numeric aspect ratio of a photo, used to lock slot proportions. */
export function aspectRatioValue(aspectRatio: PositionAspectRatio): number {
  if (aspectRatio === '1x1') {
    return 1;
  }
  if (aspectRatio === '4x3') {
    return 4 / 3;
  }
  return 3 / 2;
}

/**
 * Height that keeps a slot at the template's photo aspect ratio. Normalised width and height are
 * relative to different axes, so the background dimensions have to come back in.
 *
 * At 0 degrees a photo lies flat, so the slot is landscape. `portrait` is for slots staff has
 * stood upright, which then hold the photo rotated.
 */
export function lockedHeight(width: number, aspectRatio: PositionAspectRatio,
                             backgroundWidth: number, backgroundHeight: number,
                             portrait: boolean = false): number {
  const ratio = aspectRatioValue(aspectRatio);
  return portrait
    ? (width * backgroundWidth * ratio) / backgroundHeight
    : (width * backgroundWidth) / (ratio * backgroundHeight);
}

/**
 * How much of the photo gets cut when it is covered into a slot whose shape does not match it, and
 * which edges lose it. The keyring window is 521x812, a ratio no camera shoots, so some crop is
 * unavoidable there and staff needs to see exactly how much.
 */
export function cropInfo(position: IPrintTemplatePosition, aspectRatio: TemplateAspectRatio,
                         backgroundWidth: number, backgroundHeight: number,
                         portrait: boolean): { percent: number; edges: string } {
  if (!aspectRatio || !backgroundWidth || !backgroundHeight || !position.height) {
    return { percent: 0, edges: '' };
  }

  const slotRatio = (position.width * backgroundWidth) / (position.height * backgroundHeight);
  const photo = aspectRatioValue(<PositionAspectRatio>aspectRatio);
  const photoRatio = portrait ? 1 / photo : photo;

  if (Math.abs(slotRatio - photoRatio) < 0.0001) {
    return { percent: 0, edges: '' };
  }

  // Cover keeps the slot filled, so the photo loses whichever axis it has to spare.
  const percent = (1 - Math.min(slotRatio, photoRatio) / Math.max(slotRatio, photoRatio)) * 100;
  return {
    percent: percent,
    edges: photoRatio > slotRatio ? 'left and right' : 'top and bottom',
  };
}
